import React, { useState } from "react"
import { useGeneral } from "../context/general"
import useSortedGraph from "../hooks/useSortedGraph"
import useTickers from "../hooks/useTickers"
import useSWR from "swr"
import CompanyLogo from "./CompanyLogo"
import MenuBar from "./ui/menuBar/MenuBar"
import PercentageWithArrow from "./ui/PercentageWithArrow"
import SparklineGraph from './Graphs/SparklineGraph'
import { Skeleton, MenuItem, Divider, CardMedia, Paper } from '@mui/material'
import { marginToPaddingStyles } from './Graphs/graphUtils'
import styles from './FeedAlerts.module.scss'

const graphMargin = {top: 4, bottom: 4, right: 4, left: 8}
const skeletonPadding = marginToPaddingStyles(graphMargin)

const AlertMenu = ({menuItems}) => {
    const [anchorEl, setAnchorEl] = useState(null)
     return (<div>
        <MenuBar setAnchorEl={setAnchorEl} anchorEl={anchorEl} color='disabled'>
            {menuItems()}
        </MenuBar>
    </div>)
}

const alertsChange = {
    "monthly": "MoM",
    "daily": "DoD",
    "weekly": "WoW",
    "quarterly": "QoQ",
    "yearly": "YoY"
}

const AlertTable = ({alert, privateCompany}) => {
    const { layer } = useSortedGraph(alert.ticker, alert.dataset, alert.column, privateCompany, true, alert.time_period, alert.triggered_at)
    const stockColor = alert.change > 0 ? styles['stock-up'] : styles['stock-down']

    return (<div className={styles['alert-table']} >
        <div className={styles['dataset-and-column']}>
            <div className={styles['secondery-text']}>Alternative KPI</div>
            <div>
                <div className={styles['dataset']}>{alert.datasetName}</div>
                <div className={styles['secondery-text']}>{alert.columnName}</div>
            </div>
        </div>
        <Divider orientation="vertical" style={{height:'80px'}}/>
        <div className={styles['growth']}>
            <div className={styles['secondery-text']}>{alertsChange[alert.time_period]} Growth</div>
            <div>{isNaN(alert.change_when_triggered) ? '-' : <PercentageWithArrow number={alert.change_when_triggered.toFixed(2)} makePrecent={false}/>}</div>
        </div>
        <Divider orientation="vertical" style={{height:'80px'}}/>
        <div>
            <div className={styles['secondery-text']}>Alert Rule</div>
            <div className={styles['alert-rule-content']}>
                <PercentageWithArrow number={alert.change} makePrecent={false}/>
                <div className={stockColor}> {alertsChange[alert.time_period]}</div>
            </div>
        </div>
        <Divider orientation="vertical" style={{height:'80px'}}/>
        <CardMedia className={styles["projectCard-graph"]}>
            {layer?.data
                ? <SparklineGraph graph={[layer]} margin={graphMargin} />
                : <div style={skeletonPadding}>
                    <Skeleton variant="rect" width="100%" height="100%" />
                </div>
            }
        </CardMedia>
    </div>)
}

export default function FeedAlert({ticker, alerts}){
    const { checkPrivateCompany} = useTickers()
    const { handleCreateProjectAndOpen, tickers } = useGeneral()
    const privateCompany = tickers ? checkPrivateCompany(ticker) : null
    const { data: tickerInfo } = useSWR(`/api/getInfo?ticker=${ticker}`)

    const date = alerts && new Date(alerts[0]['triggered_at'])
    const alertDate = `${date.toLocaleString('en-US', { month: 'short' })} ${date.getDate()} ${date.getFullYear()}`

    const companyName = tickers?.find(data => data.value === ticker)?.name

    const handleOpenResearch = () => handleCreateProjectAndOpen(ticker)

    const menuItems = () => {        
        return (
            <div>
                <MenuItem onClick={handleOpenResearch}>Open Research</MenuItem>
            </div>
        )
    }

    const alertText = () => {
        const datasetsArray = new Set()
        for (let alert of alerts) datasetsArray.add(alert.datasetName)
        return `${[...datasetsArray].join(', ')} alert${datasetsArray.size > 1 ? 's' : ''} triggered`
    }

    return (
    <Paper className={styles['feed-alert']} onClick={handleOpenResearch}>
        <div className={styles['header']}>
            <div className={styles['alert-date']}>{alertDate}</div>
            <AlertMenu menuItems={menuItems}/>
        </div>
        <Divider/>
        <div className={styles['ticker-data']}>
            <div>
                <CompanyLogo image={tickerInfo?.image} website={tickerInfo?.website } ticker={ticker} size={52}/>
            </div>
            <div className={styles['ticker-names']}>
                <div className={styles['company-name']}>{companyName}</div>
                <div className={styles['ticker']}>{ticker}</div>
            </div>
        </div>
        <div className={styles['alert-text']}>{alertText()}</div>
        <div className={styles['table-container']}>
            {alerts.map((alert, index) => <div key={`${alert.ticker}_${index}`}>
                <AlertTable alert={alert} privateCompany={privateCompany} key={alert.uuid}/>
                {index < alerts.length - 1 && <Divider/>}
            </div>)}
        </div>
    </Paper>)
}